/* eslint-disable react/no-unstable-nested-components */
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import { useRef, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Select,
  Button,
  MenuItem,
  Backdrop,
  Container,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  FormHelperText,
  CircularProgress,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { createTicket, createTicketAnonymously } from 'src/services/ticket.service';

import FormFooter from './FormFooter';
import { scrollbarStyle } from '../style';
import FormPreview from '../form/FormPreview';
import { useAuth } from '../../hooks/useAuth';
import { FormProvider } from '../../hooks/useForm';
import { cardStyle, boxStyleProgress } from './style';
import { fetchFromLinkById } from '../../services/form.link.service';
import {
  fetchFormByIdAnyOne,
  fetchFormsByDepartments,
} from '../../services/form.service';

export default function TicketForm({ departments, favoriteForm }) {
  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    mode: 'all',
    defaultValues: { title: '', email: '' },
  });

  const navigate = useNavigate();
  const { showToast, hideToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [forms, setForms] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  const departmentAnchorRef = useRef(null);
  const [selectedReadIDs] = useState([]);
  const [selectedWriteIDs] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  const [backdropOpen, setBackdropOpen] = useState(false);
  // eslint-disable-next-line
  const [useFavorite, setUseFavorite] = useState(true);
  const [searchParams] = useSearchParams();
  const formToken = searchParams.get('form');

  const { isAuthenticated, departmentIds } = useAuth();
  const { t, i18n } = useTranslation();
  const [formSharableSettings, setFormSharableSettings] = useState({});
  const { isSmallScreen } = useResponsiveScreen();

  const checkIsPermitted = useCallback(
    (dep) => {
      if (!isAuthenticated) return true;
      const safeIds = departmentIds || [];

      // Barrier 1: Directly belongs to the department
      if (safeIds.includes(dep.id)) return true;

      // Barrier 2: The department's whitelist allows one of the user's departments (using flat IDs)
      if (
        dep.allowedSenderDepartmentIds &&
        dep.allowedSenderDepartmentIds.some((allowedId) => safeIds.includes(allowedId))
      ) {
        return true;
      }
      return false;
    },
    [isAuthenticated, departmentIds]
  );

  const getFormsByDepartment = useCallback(
    async (deptId) => {
      genericApiToaster(
        fetchFormsByDepartments,
        [[deptId]],
        '',
        t('errorMessages.cannotLoadForms'),
        showToast,
        hideToast,
        t,
        setForms
      );
    },
    [showToast, hideToast, t]
  );

  const addTicket = async (payload) => {
    const {
      userId,
      data,
      priority,
      duration,
      category,
      attachments,
      description,
      agents,
      autoAssign,
      tags,
      startTime,
      endTime,
      isHidden,
    } = payload;

    const ticketTitle = isHidden && formToken ? 'survey' : getValues('title');
    const guestEmail = getValues('email');

    const ticketRequest = {
      title: ticketTitle,
      description,
      status: 'UNASSIGNED',
      status_id: 1,
      priority: priority || 'LOW',
      estimatedDuration: duration,
      category,
      createdBy: userId || -1,
      department_id: selectedDepartment,
      data,
      agents,
      autoAssign,
      tags,
      startTime,
      endTime,
      hidden: isHidden,
      form_id: selectedForm?.id,
      permissions: { read: selectedReadIDs, write: selectedWriteIDs },
    };

    const formData = attachments;
    const jsonBlob = new Blob([JSON.stringify(ticketRequest)], {
      type: 'application/json',
    });
    formData.append('ticketRequest', jsonBlob);

    const onProgress = (e) => setProgress(Math.round((e.loaded / e.total) * 100));

    if (isAuthenticated) return createTicket(formData, onProgress);
    return createTicketAnonymously(formData, onProgress, formToken, guestEmail);
  };

  const handleGetForm = useCallback(
    async (id) => {
      genericApiToaster(
        fetchFormByIdAnyOne,
        id,
        '',
        t('errorMessages.couldntLoadForm'),
        showToast,
        hideToast,
        t,
        (d) => {
          setSelectedForm(d);
          setShowForm(true);
        }
      );
    },
    [showToast, hideToast, t]
  );

  useEffect(() => {
    if (
      !formToken ||
      (formToken && formSharableSettings && !formSharableSettings.isHidden)
    ) {
      setError('title', { type: 'manual', message: t('formControl.enterTitle') });
    } else {
      clearErrors('title');
    }

    if (formToken && formSharableSettings && !formSharableSettings.isAnonymous) {
      setError('email', { type: 'manual', message: t('formControl.enterEmail') });
    } else {
      clearErrors('email');
    }
  }, [setError, t, formToken, formSharableSettings, clearErrors]);

  useEffect(() => {
    if (favoriteForm?.departmentId) {
      getFormsByDepartment(favoriteForm.departmentId);
      setSelectedDepartment(favoriteForm.departmentId);
      setShowForm(true);
    }
  }, [favoriteForm, getFormsByDepartment]);

  useEffect(() => {
    const run = async () => {
      if (formToken) {
        try {
          const decoded = jwtDecode(formToken);

          if (decoded.lang && i18n.language !== decoded.lang) {
            await i18n.changeLanguage(decoded.lang);
          }

          const clientId = secureLocalStorage.getItem('clientId') || crypto.randomUUID();
          secureLocalStorage.setItem('clientId', clientId);
          const link = await fetchFromLinkById(decoded.formLinkId, formToken, clientId);
          setFormSharableSettings(link);
          setHidden(link.isHidden);

          if (link.isActive && new Date() < new Date(link.expiresOn)) {
            handleGetForm(decoded.formId);
            if (link.form?.departmentId) {
              setSelectedDepartment(link.form.departmentId);
            }
          } else {
            navigate('/form-unavailable');
          }
        } catch (e) {
          if (e.response?.status === 429) {
            navigate('/too-many-requests');
          } else {
            navigate('/form-unavailable');
          }
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formToken]);

  return (
    <Box
      sx={{
        backgroundImage: formToken ? 'url("/assets/images/covers/image.png")' : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: formToken ? (isSmallScreen ? '30px' : '60px') : '0px',
      }}
    >
      <Container sx={formToken ? cardStyle : {}}>
        <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={backdropOpen}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={progress} />
            <Box sx={boxStyleProgress}>
              <Typography variant="caption" color="white">{`${progress}%`}</Typography>
            </Box>
          </Box>
        </Backdrop>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mt={formToken ? 3 : 1}
        >
          <Typography variant="h4" sx={{ fontSize: isSmallScreen && '0.875rem' }}>
            {hidden && formToken
              ? t('surveyDashboard.createsurvey')
              : t('pages.ticketDetails.createTicket')}
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit(() => {})}>
          <Stack spacing={isSmallScreen ? 2 : 3}>
            <Box />
            {!formToken && (
              <FormControl
                id="select-department-form-control"
                ref={departmentAnchorRef}
                fullWidth
                error={!!errors.department}
                size={isSmallScreen ? 'small' : 'medium'}
              >
                <Autocomplete
                  id="select-department"
                  options={(departments || []).filter(checkIsPermitted)}
                  getOptionLabel={(option) => option.name || ''}
                  value={departments?.find((d) => d.id === selectedDepartment) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedForm(null);
                      setUseFavorite(false);
                      getFormsByDepartment(newValue.id);
                      setSelectedDepartment(newValue.id);
                    }
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        ...scrollbarStyle,
                        '& .MuiAutocomplete-listbox': {
                          p: 1,
                          '& .MuiMenuItem-root': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            py: 1.5,
                          },
                        },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('attributes.department')}
                      error={!!errors.department}
                    />
                  )}
                />
                <FormHelperText>{errors.department?.message}</FormHelperText>
              </FormControl>
            )}
            {showForm ? (
              <>
                {!formToken && (
                  <>
                    {/* Fallback empty-state rendered when a department has no forms */}
                    {forms.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, width: '100%' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            mb: 2,
                            fontSize: isSmallScreen ? '0.85rem' : '1.1rem',
                          }}
                        >
                          {t('pages.formPage.noFormsInDepartment')}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowForm(false);
                            setSelectedDepartment(undefined);
                            setForms([]);
                          }}
                          sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}
                        >
                          {t('buttons.back')}
                        </Button>
                      </Box>
                    ) : (
                      <FormControl fullWidth size={isSmallScreen ? 'small' : 'medium'}>
                        <InputLabel>{t('nav.forms')}</InputLabel>
                        <Select
                          label={t('nav.forms')}
                          value={selectedForm?.id || ''}
                          onChange={(e) =>
                            setSelectedForm(forms.find((f) => f.id === e.target.value))
                          }
                          disabled={!!formToken}
                        >
                          {forms?.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              <Typography variant="body2">{f.title}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
                {/* Dynamically hides the title input completely until a form is selected (for in-app routes) */}
                {(formToken
                  ? formSharableSettings && !formSharableSettings.isHidden
                  : !!selectedForm) && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <TextField
                      {...register('title', {
                        required: t('formControl.enterTitle'),
                        onChange: (e) => {
                          if (e.target.value.trim() !== '') {
                            clearErrors('title');
                          } else {
                            setError('title', {
                              type: 'manual',
                              message: t('formControl.enterTitle'),
                            });
                          }
                        },
                      })}
                      error={!!errors.title}
                      helperText={errors.title?.message}
                      label={t('attributes.title') || 'Title'}
                      fullWidth
                      size={isSmallScreen ? 'small' : 'medium'}
                    />
                  </Box>
                )}

                {formToken &&
                  formSharableSettings &&
                  !formSharableSettings.isAnonymous && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <TextField
                        {...register('email', {
                          required: t('formControl.enterEmail') || 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message:
                              t('formControl.invalidEmail') || 'Invalid email address',
                          },
                          onChange: (e) => {
                            if (e.target.value.trim() !== '') {
                              clearErrors('email');
                            } else {
                              setError('email', {
                                type: 'manual',
                                message:
                                  t('formControl.enterEmail') || 'Email is required',
                              });
                            }
                          },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        label={t('attributes.email') || 'Email'}
                        fullWidth
                        size={isSmallScreen ? 'small' : 'medium'}
                      />
                    </Box>
                  )}

                {selectedForm?.formData && (
                  <FormProvider key={selectedForm.id} initialData={selectedForm.formData}>
                    <FormPreview
                      disabled={
                        !formToken || (formToken && !formSharableSettings?.isAnonymous)
                          ? !isValid
                          : false
                      }
                      addTicket={addTicket}
                      handleBackdropClose={() => setBackdropOpen(false)}
                      handleBackdropOpen={() => {
                        setProgress(0);
                        setBackdropOpen(true);
                      }}
                      formSharableSettings={formSharableSettings}
                    />
                  </FormProvider>
                )}
              </>
            ) : null}
            {!formToken && !showForm && (
              <Stack direction="row" justifyContent="center" spacing={2}>
                <LoadingButton
                  type="button"
                  variant="contained"
                  onClick={() => setShowForm(true)}
                  disabled={!selectedDepartment}
                >
                  {t('buttons.next')}
                </LoadingButton>
                <LoadingButton
                  type="button"
                  sx={{ bgcolor: '#b90e0a' }}
                  variant="contained"
                  onClick={() => navigate('/dashboard/tickets')}
                >
                  {t('buttons.cancel')}
                </LoadingButton>
              </Stack>
            )}
            {formToken && <FormFooter show />}
          </Stack>
        </form>
      </Container>
    </Box>
  );
}

TicketForm.propTypes = {
  departments: PropTypes.array,
  favoriteForm: PropTypes.shape({
    departmentId: PropTypes.number,
    id: PropTypes.string,
    formData: PropTypes.object,
  }),
};
